import { useCallback, useEffect, useState } from "react";
import { Plus, FileSpreadsheet, FileDown, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { EmptyState, PageHeader, StatCard } from "../../components/Common";
import { RangeFilter, MiniChart } from "../../components/RangeFilter";
import { presetRange, rangeLabel, rangeParams, singleMonth } from "../../lib/range";
import { TripList } from "../../components/TripList";
import { TripFormDialog } from "../../components/TripFormDialog";
import { ProofDialog } from "../../components/ProofDialog";
import { useAuth } from "../../context/AuthContext";
import { api, downloadFile, formatApiError, formatIDR } from "../../lib/api";

export default function DriverHome() {
  const { user } = useAuth();
  const [range, setRange] = useState(() => presetRange("month"));
  const month = singleMonth(range);
  const [trips, setTrips] = useState([]);
  const [summary, setSummary] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [proofId, setProofId] = useState(null);

  const load = useCallback(async () => {
    const params = rangeParams(range);
    const [t, s] = await Promise.all([api.get("/trips", { params }), api.get("/summary", { params })]);
    setTrips(t.data);
    setSummary(s.data);
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const onDelete = async (t) => {
    try { await api.delete(`/trips/${t.id}`); toast.success("Trip dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const exportAs = (fmt) => downloadFile("/export", { format: fmt, ...rangeParams(range) }, `report-${range.start || "awal"}_${range.end || "akhir"}.${fmt}`).catch((e) => toast.error(formatApiError(e)));
  const orders = trips.filter((t) => t.status === "order").sort((a, b) => a.trip_date.localeCompare(b.trip_date));
  const done = trips.filter((t) => t.status !== "order");
  const edit = (t) => { setEditing(t); setFormOpen(true); };

  return (
    <div>
      <PageHeader eyebrow={`Halo, ${user.name}`} title="Report Saya">
        <Button onClick={openNew} className="h-12 sm:h-10 w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="add-trip-btn">
          <Plus className="h-4 w-4 mr-1" /> Tambah Trip
        </Button>
      </PageHeader>
      <RangeFilter value={range} onChange={setRange} />

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="col-span-2"><StatCard tone="dark" label="Total Pendapatan" value={formatIDR(summary.total)} hint={`${summary.trips} trip · ${summary.pax} orang`} testId="stat-total" /></div>
          <StatCard label="Order Baru" value={summary.orders} testId="stat-orders" delay={20} />
          <StatCard label="Collect (dari tamu)" value={formatIDR(summary.collect)} testId="stat-collect" delay={40} />
          <StatCard label="Sudah Dibayar" value={formatIDR(summary.paid)} testId="stat-paid" delay={80} />
          <StatCard label="Belum Dibayar" value={formatIDR(summary.unpaid)} testId="stat-unpaid" delay={160} />
          <StatCard label="Susuk (terpisah)" value={formatIDR(summary.susuk)} testId="stat-susuk" delay={240} />
        </div>
      )}
      {summary && <MiniChart data={summary.per_day} />}

      {orders.length > 0 && (
        <section className="mb-10" data-testid="driver-orders">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">Order Masuk <span className="ml-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-bold text-sky-700" data-testid="driver-order-count">{orders.length}</span></h2>
          <p className="text-sm text-slate-500 mb-4">Setelah trip selesai, tekan “Lengkapi” untuk mengisi pax final, aqua, extra dan susuk.</p>
          <TripList trips={orders} onEdit={edit} onDelete={onDelete} onViewProof={setProofId} />
        </section>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-bold text-slate-900">Daftar Trip</h2>
        <div className="flex flex-wrap justify-end gap-2">
          {month && <Button variant="outline" size="sm" onClick={() => downloadFile("/slip", { month }, `slip-${month}.pdf`).catch((e) => toast.error(formatApiError(e)))} data-testid="slip-btn"><FileText className="h-4 w-4 mr-1" />Slip PDF</Button>}
          <Button variant="outline" size="sm" onClick={() => exportAs("csv")} data-testid="export-csv-btn"><FileDown className="h-4 w-4 mr-1" />CSV</Button>
          <Button variant="outline" size="sm" onClick={() => exportAs("xlsx")} data-testid="export-xlsx-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
        </div>
      </div>

      {done.length ? (
        <TripList trips={done} onEdit={edit} onDelete={onDelete} onViewProof={setProofId} />
      ) : (
        <EmptyState title="Belum ada trip" text="Tekan “Tambah Trip” untuk membuat report pertama bulan ini." testId="empty-trips" />
      )}

      <TripFormDialog open={formOpen} onOpenChange={setFormOpen} trip={editing} onSaved={() => load()} />
      <ProofDialog paymentId={proofId} onClose={() => setProofId(null)} />
    </div>
  );
}
